import React, { useState, useRef } from "react";
import "./pages.css";
import { FileText, CheckCircle } from "lucide-react";
import { formatDate } from "../util.js"

import structure from "../images/Management Structure.png"

// change this whenever the ToU is updated
const LAST_UPDATED = new Date(2025, 5-1, 11);

const Terms = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const termsRef = useRef(null);

  const handleScroll = () => {
    if (termsRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = termsRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const toggleCheckbox = () => {
    if (hasScrolledToBottom) {
      setIsChecked(!isChecked);
    }
  };

  return (
    <div className="help-container">
      <div className="help-header">
        <h1>Terms of Use</h1>
        <FileText size={40} color="#005EB8" className="mb-4" />
      </div>

      <div className="settings-card">
        <div className="settings-card-header">
          <h2>Management Structure</h2>
        </div>
        <div className="settings-card-body">
          <div className="management-image-container" style={{ 
            width: "100%", 
            textAlign: "center", 
            marginBottom: "var(--spacing-md)" 
          }}>
            <img 
              src={structure} 
              alt="Management Structure" 
              style={{
                maxWidth: "100%",
                borderRadius: "var(--border-radius-md)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
              }}
            />
          </div>

          <h4 className="mt-4 mb-2">Management Explained</h4>
          <p>Team November represents the pinnacle of collaborative innovation in healthcare software development. Under the visionary guidance of our client, Dr Luke Farrow, our exceptional team has crafted PATHWAY–an elegant solution to the complex challenges of patient waiting list management.</p>
          <p className="mb-4">The synergy between Nusrath Jinnath's exceptional technical leadership and frontend expertise, Piotr Weychan's backend architecture expertise, and Muhammad Vohra's intuitive frontend design has resulted in a platform that seamlessly blends functionality with user experience. Together, we've transformed the conventional approach to healthcare operations into a streamlined, patient-centred system that empowers medical professionals and enhances care coordination.</p>
        </div>
      </div>

      <div className="settings-card mt-4">
        <div className="settings-card-header">
          <h2>Licence Agreement</h2>
        </div>
        <div className="settings-card-body">
          <div 
            ref={termsRef}
            onScroll={handleScroll}
            className="licence-agreement-container"
            style={{
              height: "300px",
              overflowY: "scroll",
              border: "1px solid var(--border-colour)",
              padding: "var(--spacing-lg)",
              marginBottom: "var(--spacing-md)",
              backgroundColor: "var(--background-lighter)",
              lineHeight: "1.6",
              fontSize: "15px"
            }}
          >
            <div className="licence-header" style={{ marginBottom: "24px", textAlign: "center" }}>
              <h3 style={{ color: "#005EB8", marginBottom: "16px" }}>Licence</h3>
              <p style={{ fontWeight: "bold" }}>Copyright © 2025 Piotr A Weychan, Nusrath Jinnath, Muhammad Vohra</p>
              <p style={{ fontWeight: "bold", marginBottom: "16px" }}>All rights reserved.</p>
            </div>

            <div className="licence-section">
              <p>Permission is granted to the University of Aberdeen to store, use, and reproduce this work strictly for internal academic purposes only, including but not
              limited to plagiarism detection, teaching, moderation, and quality assurance.</p>
              
              <p style={{ fontWeight: "bold", marginTop: "16px" }}>No commercial use, redistribution, sublicensing, or derivative use by the University is permitted without the express, prior, written consent of the Management Team.</p>
              
              <p style={{ marginTop: "16px" }}>Contributors who have been formally recognised as having made <em>significant and meaningful contributions</em> – as determined solely and exclusively by the
              Management Team (see below) – are granted a <strong>limited, non-transferable, non-sublicensable, exclusive licence</strong> to:</p>
              
              <ul style={{ margin: "16px 0", paddingLeft: "24px" }}>
                <li style={{ marginBottom: "8px" }}>Use, modify, and reproduce this work;</li>
                <li>Incorporate this work into personal academic or professional portfolios, including but not limited to applications for employment or further education.</li>
              </ul>
              
              <p style={{ fontWeight: "bold" }}>This licence does not confer any rights to distribute, publish, sublicense, or commercially exploit the work.</p>
              <p style={{ fontWeight: "bold", marginBottom: "16px" }}>No rights – whether express, implied, or statutory – are granted in relation to patents, patent applications, or other forms of registered intellectual property.</p>
              
              <p>All licences may be <strong>revoked at any time, without notice or explanation</strong>, at the sole discretion of the Management Team. All rights not expressly granted are reserved.</p>
            </div>
            
            <div className="licence-section" style={{ margin: "24px 0", padding: "16px 0", borderTop: "1px solid #eaeaea", borderBottom: "1px solid #eaeaea" }}>
              <h4 style={{ color: "#005EB8", marginBottom: "16px" }}>Management Team (Primary Authors)</h4>
              
              <p>The following individuals collectively constitute the Management Team and are recognised as the Primary Authors and sole rights-holders of this work:</p>
              
              <ul style={{ margin: "16px 0", paddingLeft: "24px" }}>
                <li style={{ marginBottom: "8px" }}>Piotr A Weychan</li>
                <li style={{ marginBottom: "8px" }}>Nusrath Jinnath</li>
                <li>Muhammad Vohra</li>
              </ul>
              
              <p style={{ marginTop: "16px" }}>The Management Team retains full and exclusive authority over all intellectual property decisions related to this work, including but not limited to:</p>
              
              <ul style={{ margin: "16px 0", paddingLeft: "24px" }}>
                <li style={{ marginBottom: "8px" }}>Contributor recognition;</li>
                <li style={{ marginBottom: "8px" }}>Access permissions;</li>
                <li style={{ marginBottom: "8px" }}>Rights revocation;</li>
                <li style={{ marginBottom: "8px" }}>Commercial licensing;</li>
                <li>Authorisation for reuse, reproduction, or adaptation.</li>
              </ul>
              
              <p style={{ fontWeight: "bold", marginTop: "16px" }}>All decisions made by the Management Team are final, binding, and not subject to review or appeal.</p>
            </div>
            
            <div className="licence-section" style={{ margin: "24px 0", padding: "16px 0", borderBottom: "1px solid #eaeaea" }}>
              <h4 style={{ color: "#005EB8", marginBottom: "16px" }}>Recognised Significant Contributors</h4>
              
              <p>The following individuals have been granted rights under the licence terms stated above:</p>
              
              <ul style={{ margin: "16px 0", paddingLeft: "24px" }}>
                <li style={{ marginBottom: "8px" }}>Meike Schneider</li>
                <li>Sören Schneider</li>
              </ul>
              
              <p style={{ marginTop: "16px" }}>No other individuals or entities hold rights to access, use, reproduce, or derive works from this material unless explicitly authorised in writing by the Management Team.</p>
            </div>
            
            <div className="licence-section" style={{ marginTop: "24px" }}>
              <h4 style={{ color: "#005EB8", marginBottom: "16px" }}>Legal Disclaimer</h4>
              
              <p>This work is provided <strong>"as is"</strong>, without warranty of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, non-infringement, or accuracy.</p>
              <p style={{ marginTop: "16px" }}>Under no circumstances shall the authors, the Management Team, or any affiliated institutions be held liable for any damages, losses, or claims arising out of or in connection with the use, reproduction, or interpretation of this work.</p>
            </div>
          </div>
          
          <div className="terms-acceptance">
            <label className="checkbox-container" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input 
                type="checkbox" 
                checked={isChecked} 
                onChange={toggleCheckbox} 
                disabled={!hasScrolledToBottom} 
              />
              <span className={`checkmark ${!hasScrolledToBottom ? 'disabled' : ''}`}></span>
              <span>
                I have read and agree to the Terms of Use
                {!hasScrolledToBottom && (
                  <span className="text-muted"> (please scroll to the end to accept)</span>
                )}
              </span>
            </label>
          </div>
          
          <button 
            className={`btn ${isChecked ? 'btn-primary' : 'btn-secondary'} settings-button mt-3`}
            disabled={!isChecked}
          >
            {isChecked ? (
              <>
                <CheckCircle size={18} className="me-2" /> Accept Terms
              </>
            ) : (
              'Accept Terms'
            )}
          </button>
        </div>
      </div>
      
      <div className="help-footer mt-4">
        <p>© 2024-{(new Date()).toLocaleString("en-GB", {year: "numeric"})} PATHWAY by Team November. All rights reserved.</p>
        <p><strong>Last updated</strong>: {formatDate(LAST_UPDATED)}</p>
      </div>
    </div>
  );
};

export default Terms;